<template>
  <div class="print">
    <main class="sheet">
      <img class="printlogo" :src="managingCompany.logoUrl" />
      <h1>
        {{ currentProject.title }}
        <span>{{ subtitle }}</span>
        <span>{{ now() }} ({{ username }})</span>
      </h1>

      <div class="editChild">
        <label for="subtitle">{{ $t('Sub Title') }}</label>
        <input style="border: 1px solid black" type="text" id="subtitle" v-model="subtitle" />
        <br />
        <br />
        <label for="a4">{{ $t('Page Size') }}</label>
        <br />
        ({{ pageSizes[selectedPageSize][selectedPageLayout].width }}mm x
        {{ pageSizes[selectedPageSize][selectedPageLayout].height }}mm)
        <br />
        <input type="radio" id="a4" value="A4" v-model="pagesize" />
        <label for="a4">A4</label>
        <br />
        <input type="radio" id="a3" value="A3" v-model="pagesize" />
        <label for="A3">A3</label>
        <br />
        <input type="radio" id="a2" value="A2" v-model="pagesize" />
        <label for="A2">A2</label>
        <br />
        <input type="radio" id="a1" value="A1" v-model="pagesize" />
        <label for="A1">A1</label>
        <br />
        <br />
        <div
          class="previewPaper"
          :style="{
            height:
              (pageSizes[selectedPageSize][selectedPageLayout].height /
                pageSizes[selectedPageSize][selectedPageLayout].width) *
                10 +
              'mm',
          }"
        >
          <div :style="{ height: lastPageFillFactor + '%' }">
            {{ papersNeeded }}
          </div>
        </div>
        <label for="portrait">{{ $t('Page Layout') }}</label>
        <br />
        <input type="radio" id="portrait" value="portrait" v-model="pagelayout" />
        <label for="portrait">{{ $t('Portrait') }}</label>
        <br />
        <input type="radio" id="landscape" value="landscape" v-model="pagelayout" />
        <label for="landscape">{{ $t('Landscape') }}</label>
        <br />
        <br />
        <label for="increase">{{ $t('Row Height') }}</label>
        <br />
        <button id="increase" @click="increaseRowHeight">+</button>
        {{ pageSizes[selectedPageSize].rowHeight.toFixed(1) }}
        <button id="decrease" @click="decreaseRowHeight">-</button>
        ({{ optimalRowHeight }}) <br /><br />
        <button @click="print">{{ $t('Print') }}</button>

        <router-link
          :to="{ name: 'ProjectPlanEdit', params: { _id: this.$route.params._id } }"
          v-if="$route.name === 'ProjectPlanPrint'"
        >
          {{ $t('Close Preview') }}
        </router-link>
      </div>
      <div class="titles" :style="{ left: titlePosition }">
        <div
          class="monthTitle"
          :style="{ height: 6.6 + allChildrenHeight + 'mm', left: monthPos(month), width: monthWidth(month) }"
          v-for="month in shownMonths"
          :key="'m' + month"
        >
          {{ getMonthDisplay(month) }}
        </div>
        <br />
        <div
          class="weekTitle"
          :class="{ activePeriod: isActiveWeek(week) }"
          :style="{
            fontSize: weekFontSize,
            lineHeight: weekFontSize,
            height: 4.4 + allChildrenHeight + 'mm',
            left: weekPos(week),
            width: weekWidth,
          }"
          v-for="week in shownWeeks"
          :key="'w' + week"
        >
          {{ getWeekDisplay(week) }}
        </div>
        <br />
        <div
          v-if="dayWidth > 2.4"
          class="dayTitle"
          :class="[dayHighLight(day)]"
          :style="{
            left: dayPos(day),
            width: 2.0 + allChildrenHeight + 'mm',
            height: dayWidth + 'mm',
            lineHeight: dayWidth + 'mm',
          }"
          v-for="day in shownWeeks * 7"
          :key="'d' + day"
        >
          {{ getDayDisplay(day) }}
        </div>
      </div>
      <div
        :style="{ width: titleWidth + 'mm' }"
        style="position: absolute; top: 15mm; bottom: 0mm; background: white"
      ></div>
      <div ref="outerParts" id="outerParts" class="outerParts">
        <div id="innerParts" :style="{ width: shownWeeksWidth }">
          <div class="stage" v-for="(stage, index) in stages" :key="stage._id">
            <StagePrint
              :titleWidth="titleWidth"
              :treeChild="stage"
              :showAlerts="showAlerts"
              v-if="stage.children"
              :level="0"
              :children="stage.children"
              :stage="fetchStage(stage._id)"
              :fontSize="fontSize"
              :rowHeight="pageSizes[selectedPageSize].rowHeight"
              :filterText="filterText"
              :filterDate="filterDate"
              :filterDateEnd="filterDateEnd"
              :filterDateZoom="zoom"
            />
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import StagePrint from './StagePrint';
import moment from 'moment';
import * as stageHelpers from '@/common/stageHelpers';
import CommonHeader from '@/components/toolbar/CommonHeader';
import CommonFooter from '@/components/toolbar/CommonFooter';

export default {
  name: 'ProjectPlanPrint',
  props: {
    zoom: {
      type: Number,
      default: 1,
    },
    filterText: {
      type: String,
      default: '',
    },
    filterDate: {
      type: String,
      default: '2017-01-01',
    },
    filterDateEnd: {
      type: String,
      default: '2017-12-31',
    },
    filterDeliveries: {
      type: Boolean,
      default: false,
    },
    filterManagerId: {
      type: String,
      default: '',
    },
    showAlerts: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      scrollLeft: 0,
      scrollTop: 0,
      selectedPageSize: 'A3',
      selectedPageLayout: 'landscape',
      cssPagedMedia: null,
      subtitle: '',
      pageSizes: {
        A4: {
          rowHeight: 2.7,
          fontSize: 1.9,
          portrait: {
            width: 210.0,
            height: 297.0,
          },
          landscape: {
            width: 297.0,
            height: 210.0,
          },
        },
        A3: {
          rowHeight: 3.3,
          fontSize: 2.5,
          portrait: {
            width: 297.0,
            height: 420.0,
          },
          landscape: {
            width: 420.0,
            height: 297.0,
          },
        },
        A2: {
          rowHeight: 3.3,
          fontSize: 2.5,
          portrait: {
            width: 420.0,
            height: 594.0,
          },
          landscape: {
            width: 594.0,
            height: 420.0,
          },
        },
        A1: {
          rowHeight: 3.3,
          fontSize: 2.5,
          portrait: {
            width: 594.0,
            height: 841.0,
          },
          landscape: {
            width: 841.0,
            height: 594.0,
          },
        },
      },
    };
  },
  mounted() {
    this.cssPagedMedia = (function () {
      var style = document.createElement('style');
      document.head.appendChild(style);
      return function (rule) {
        style.innerHTML = rule;
      };
    })();

    this.cssPagedMedia.size = (size) => {
      this.cssPagedMedia('@page {size: ' + size + '}');
    };

    this.$nextTick(() => {
      // window.addEventListener('resize', this.updateZoomFactor);
      this.updateZoomFactor();
      var body = document.body;
      body.classList.add(this.selectedPageSize);
      body.classList.add(this.selectedPageLayout);
      this.cssPagedMedia.size(this.selectedPageSize + ' ' + this.selectedPageLayout);
    });
  },
  beforeDestroy() {
    var body = document.body;
    body.classList.remove(this.selectedPageSize);
    body.classList.remove(this.selectedPageLayout);
    // window.removeEventListener('resize', this.updateZoomFactor);
  },
  methods: {
    ...mapActions(['loadProject', 'saveProjectProperties', 'setViewSetting']),
    increaseRowHeight: function () {
      this.pageSizes[this.selectedPageSize].rowHeight += 0.1;
    },
    decreaseRowHeight: function () {
      if (this.pageSizes[this.selectedPageSize].rowHeight > this.pageSizes[this.selectedPageSize].fontSize) {
        this.pageSizes[this.selectedPageSize].rowHeight -= 0.1;
      }
    },
    now: function () {
      return moment().format('YYYY-MM-DD HH:mm');
    },
    print: function () {
      window.print();
    },
    getDateForWeek: function (showWeek) {
      var firstDay = moment(this.filterDate)
        .isoWeekday(1)
        .add(showWeek - 1, 'week');
      return firstDay.format('YYYY-MM-DD') + ' - ' + firstDay.isoWeekday(7).format('YYYY-MM-DD');
    },
    getWeekDisplay: function (showWeek) {
      var week = moment(this.filterDate)
        .isoWeekday(1)
        .add(showWeek - 1, 'week')
        .isoWeek();
      if (this.dayWidth > 1) {
        return this.$t('w.{week}', { week: week });
      } else if (this.dayWidth > 0.4) {
        return week;
      }

      return week % 5 === 0 || week === 1 ? week : '';
    },
    dayPos: function (shownDay) {
      var x = (shownDay - 1) * this.dayWidth;
      var diff = 0;
      return this.titleWidth + x + diff + 'mm';
    },
    weekPos: function (shownWeek) {
      var x = (shownWeek - 1) * 7 * this.dayWidth;
      var day = moment(this.filterDate).day() - 1;
      if (day < 0) {
        day += 7;
      }
      var diff = 1 * this.zoom * this.viewSettings.projectPlan.zoomFactorPrint * -day;

      return this.titleWidth + x + diff + 'mm';
    },
    monthPos: function (shownMonth) {
      var dateOfShownMonth = moment(this.filterDate)
        .add(shownMonth - 1, 'month')
        .date(1); // .format('YYYY-MM-DD'); //.date(1);
      var x = this.dayWidth * moment(dateOfShownMonth).diff(this.filterDate, 'days');
      return this.titleWidth + x + 'mm';
    },
    getDayDisplay: function (shownDay) {
      var day = moment(this.filterDate)
        .add(shownDay - 1, 'day')
        .format('DD');
      return day;
    },
    dayHighLight: function (shownDay) {
      var day = moment(this.filterDate).add(shownDay - 1, 'day');
      if (day.day() === 0 || day.day() === 6) {
        return 'dayHighLightWeekEnd';
      }
      return '';
    },

    getMonthDisplay: function (shownMonth) {
      var month = moment(this.filterDate).add(shownMonth - 1, 'month');
      // console.log('shownmont', month);
      if (this.dayWidth > 0.6) {
        return this.$t(month.format('MMMM')) + ' ' + month.year();
      } else if (this.dayWidth > 0.3) {
        return this.$t(month.format('MMM')) + ' ' + month.format('YY');
      }
      return this.$t(month.format('MMM'));
    },
    monthWidth: function (month) {
      var size =
        moment(this.filterDate)
          .add(month - 1, 'month')
          .daysInMonth() * this.dayWidth;
      return size + 'mm';
    },
    updateTitle: function (e) {
      this.saveProjectProperties({
        updateProject: this.currentProject,
        newProjectProperties: { title: e.target.value },
      });
    },
    isActiveWeek: function (checkWeek) {
      var week = moment(this.filterDate)
        .isoWeekday(1)
        .add(checkWeek - 1, 'week');
      return moment().isSame(week, 'week');
    },
    updateZoomFactor: function () {
      var pageWidth = this.pageSizes[this.selectedPageSize][this.selectedPageLayout].width - 10;
      // var newZoomFactor = Math.round((window.innerWidth - 210) / this.shownWeeks / 7 * 10) / 10;
      var newZoomFactor = Math.round(((pageWidth - 30.0) / (this.shownWeeks * 7.0)) * 10000.0) / 10000.0;

      // if ('s' + newZoomFactor.toFixed() !== this.viewSettings.projectPlan.zoomFactorPrint.toFixed()) {
      // console.log('updating zoomfactor y', this.viewSettings.projectPlan.zoomFactorPrint, newZoomFactor, this.pageSizes[this.selectedPageSize][this.selectedPageLayout].width, this.selectedPageLayout, this.selectedPageSize, pageWidth, this.shownWeeks);
      this.$nextTick(function () {
        this.setViewSetting({
          section: 'projectPlan',
          property: 'zoomFactorPrint',
          newValue: newZoomFactor,
        });
      });
      // }
    },
  },
  computed: {
    ...mapGetters([
      'currentProject',
      'fetchPart',
      'fetchStage',
      'filterParts',
      'managingCompany',
      'username',
      'viewSettings',
    ]),
    stages: {
      get: function () {
        return this.filterParts({
          filterText: this.filterText,
          filterDate: this.filterDate,
          filterDateEnd: this.filterDateEnd,
          partManagerId: this.filterManagerId,
          filterDeliveries: this.filterDeliveries,
        });
      },
      set: function (value) {
        console.log('project plan set list', value);
      },
    },
    titleWidth: function () {
      return 12 * this.pageSizes[this.selectedPageSize].fontSize;
    },
    pagesize: {
      get: function () {
        return this.selectedPageSize;
      },
      set: function (newPageSize) {
        var body = document.body;
        body.classList.remove(this.selectedPageSize);
        this.selectedPageSize = newPageSize;
        body.classList.add(this.selectedPageSize);
        this.cssPagedMedia.size(this.selectedPageSize + ' ' + this.selectedPageLayout);
        this.updateZoomFactor();
      },
    },
    pagelayout: {
      get: function () {
        return this.selectedPageLayout;
      },
      set: function (newPageLayout) {
        var body = document.body;
        body.classList.remove(this.selectedPageLayout);
        this.selectedPageLayout = newPageLayout;
        body.classList.add(this.selectedPageLayout);
        this.cssPagedMedia.size(this.selectedPageSize + ' ' + this.selectedPageLayout);
        this.updateZoomFactor();
      },
    },
    fontSize: function () {
      return this.pageSizes[this.selectedPageSize].fontSize;
    },
    rowHeight: function () {
      return this.pageSizes[this.selectedPageSize].rowHeight;
    },
    allChildrenHeight: function () {
      return this.visibleChildren * this.rowHeight;
    },
    allChildrenMinimumHeight: function () {
      return this.visibleChildren * this.fontSize;
    },
    optimalRowHeight: function () {
      return (
        Math.floor(
          (10 * this.papersNeededMinimum * this.pageSizes[this.selectedPageSize][this.selectedPageLayout].height) /
            this.visibleChildren
        ) / 10
      );
    },
    papersNeeded: function () {
      return Math.ceil(
        (this.allChildrenHeight + 20) / this.pageSizes[this.selectedPageSize][this.selectedPageLayout].height
      );
    },
    papersNeededMinimum: function () {
      return Math.ceil(
        (this.allChildrenMinimumHeight + 20) / this.pageSizes[this.selectedPageSize][this.selectedPageLayout].height
      );
    },
    lastPageFillFactor: function () {
      return Math.round(
        100 *
          ((this.allChildrenHeight + 20) / this.pageSizes[this.selectedPageSize][this.selectedPageLayout].height -
            this.papersNeeded +
            1)
      );
    },
    visibleChildren: function () {
      return stageHelpers.getVisibleChildren(this.$store, this.stages);
    },
    dayWidth: function () {
      var size = this.zoom * this.viewSettings.projectPlan.zoomFactorPrint;
      return size;
    },
    shownWeeks: function () {
      return -moment(this.filterDate).isoWeekday(1).diff(moment(this.filterDateEnd).isoWeekday(1), 'weeks') + 1;
    },
    weekWidth: function () {
      var size = 7 * this.dayWidth;
      return size - 1 + 'mm';
    },
    weekFontSize: function () {
      if (this.dayWidth < 0.6) {
        return '1.5mm';
      } else if (this.dayWidth < 0.7) {
        return '2.0mm';
      }
      return '2.5mm';
    },
    shownWeeksWidth: function () {
      return 21 + this.shownWeeks * 7 * this.dayWidth + 'mm';
    },
    shownMonths: function () {
      // console.log('shownMonth', -moment(this.filterDate).diff(this.filterDateEnd, 'months') + 1);
      return -moment(this.filterDate).diff(this.filterDateEnd, 'months') + 1;
    },
    titlePosition: function () {
      // var origin = 210 - this.scrollLeft;
      //
      // return (origin /* + diff*/) + 'mm';
      return '5.0mm';
    },
  },
  components: {
    CommonFooter,
    CommonHeader,
    StagePrint,
  },
  watch: {
    filterDate: function () {
      this.updateZoomFactor();
    },
    filterDateEnd: function () {
      this.updateZoomFactor();
    },
    $route: async function (to, from) {
      console.debug('ProjectPrint:watch');
      await this.loadProject(this.$route.params._id);
    },
  },
};
</script>

<style scoped>
@page {
  /* If you change here, make sure printing of projectplan does not span extra pages */
  margin: 0mm;
}

main {
  padding-top: 15px;
  margin-top: 0px;
}

button {
  background: #373e4d;
  border: 1px solid black;
  padding: 0px 2px;
  margin: 2px;
  color: white;
}

.print {
  background: white;
  color: black;
  overflow-x: hidden;
  position: relative;
}

.A1 .print {
  min-height: 840mm;
} /* One mm less in height to */
.A1.landscape .print {
  min-height: 593mm;
} /* prevent extra page when  */
.A2 .print {
  min-height: 593mm;
} /* printing one page onlye  */
.A2.landscape .print {
  min-height: 419mm;
}
.A3 .print {
  min-height: 419mm;
}
.A3.landscape .print {
  min-height: 296mm;
}
.A4 .print {
  min-height: 296mm;
}
.A4.landscape .print {
  min-height: 209mm;
}
.A5 .print {
  min-height: 209mm;
}
.A5.landscape .print {
  min-height: 147mm;
}

div {
  font-weight: normal;
}
h1 {
  font-size: 120%;
  text-align: left;
  margin: 0mm;
  margin-bottom: 2mm;
}

h1 span {
  margin-left: 10mm;
}

.title {
  width: 160mm;
  text-overflow: ellipsis;
  z-index: 0;
}

.previewPaper {
  width: 10mm;
  float: right;
  border: 1px solid black;
  overflow: hidden;
}

.previewPaper div {
  text-align: center;
  background-color: #aaa;
}

.duration {
  width: 40mm;
  text-align: right;
  padding-right: 1em;
}
.startdate {
  width: 70mm;
  text-align: right;
}

.outerParts {
  top: 2.5mm;
  left: 0mm;
  right: 0mm;
  bottom: 40mm;
  overflow: visible;
  position: relative;
  border: 0mm solid red;
  z-index: 1;
}

.titles {
  font-size: 2mm;
  white-space: nowrap;
  position: relative;
  z-index: 0;
}

.titles .monthTitle:nth-child(2n) {
  background-color: #fafafa;
}

.monthTitle {
  display: block;
  position: absolute;
  padding-left: 0.4mm;
  border-left: 0.1mm solid #eeeeee;
  height: 25mm;
  text-overflow: hidden;
}

.weekTitle {
  display: block;
  position: absolute;
  padding-left: 0.1mm;
  border-left: 0.1mm solid #aaaaaa;
  text-overflow: hidden;
}

.weekTitle:hover {
  background: #eee;
}

.dayTitle {
  font-size: 2mm;
  display: block;
  position: absolute;
  border-bottom: 0.1mm solid #bbb;
  transform-origin: 0% 0%;
  transform: rotate(90deg) translateY(-100%);
  text-overflow: hidden;
  overflow: hidden;
  vertical-align: bottom;
}

.dayTitle:hover {
  background: #eee;
}

.dayHighLightWeekEnd {
  background: #fde5c5;
}

.activePeriod {
  background: #999;
}

label {
  min-width: 8em;
  display: inline-block;
}

.printlogo {
  max-width: 10mm;
  max-height: 10mm;
  float: left;
  margin-right: 5mm;
  z-index: 10;
}

.editChild {
  background-color: white;
  width: 280px;
  right: 0px;
  position: fixed;
  bottom: 0px;
  /* Header's height is 80px; subheader's height is 3.375rem */
  top: calc(80px + 3.375rem);
  z-index: 2;
  overflow-y: scroll;
  overflow-x: hidden;
  border-left: 2px solid black;
  color: black;
  padding: 2px 10px;
}

/** For screen preview **/
@media screen {
  .titles {
  }

  .printlogo {
  }

  .A1 .A2 .A3,
  .A4 {
    border: 1px solid green;
    margin: 20mm;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/projectplanprint/ProjectPlanPrint.vue